import appJson from '../app.json';
import {StyleSheet, View} from 'react-native';
import {
  Component,
  createContext,
  Fragment,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import auth, {FirebaseAuthTypes} from '@react-native-firebase/auth';
import {
  Headline,
  ActivityIndicator,
  Provider as PaperProvider,
  Button,
  Subheading,
} from 'react-native-paper';
import SignedInStack from './signed-in/Stack';
import SignedOutStack from './signed-out/Stack';
import {
  initialWindowMetrics,
  SafeAreaProvider,
} from 'react-native-safe-area-context';
import {
  createNavigationContainerRef,
  NavigationContainer,
} from '@react-navigation/native';
import {useAppSettings} from './components/AppSettings';
import {AlertsProvider} from 'react-native-paper-alerts';
import firestore from '@react-native-firebase/firestore';
import functions from '@react-native-firebase/functions';
import {getReleaseStream, ReleaseStreams} from './AppConfig';
import {QueryClient, QueryClientProvider} from 'react-query';

// Use these to go against the firestore emulator
if (getReleaseStream() === ReleaseStreams.local) {
  auth().useEmulator('http://localhost:9099');
  firestore().settings({persistence: false});
  firestore().useEmulator('localhost', 8080);
  functions().useEmulator('localhost', 5001);
  // so on-device storage doesn't contain things wiped when emulator shuts down
  // end emulator settings
}

/**
 * Types
 */
type User = FirebaseAuthTypes.User | null;

/**
 * Contexts
 */
export const UserContext = createContext<User>(null);

export const mainNavigatorRef = createNavigationContainerRef();

const getAppScreens = () => {
  const mainScreens: {[key: string]: string} = {
    search: 'search',
    reports: 'reports',
    investor: 'investor/:type?/:id?',
    Reservations: 'reservations',
    Transactions: 'transactions',
    Properties: 'properties',
    Subscribers: 'subscribers',
    'Limited Partners': 'limited-partners',
    Members: 'members',
    People: 'people',
    'Portal Management': 'portal-management',
    'Admin Tools': 'admin-tools',
    Funds: 'funds',
    'Credit Types': 'credit-types',
    'Exchange Rates': 'exchange-rates',
    'Reservation Years': 'reservation-years',
    'Guesty Integration': 'guesty-integration',
    'Reservation Match': 'guesty-reservation-match',
    'Matched Reservations': 'guesty-matched-reservations',
    'Guest Match': 'guesty-guest-match',
    'Property Match': 'guesty-property-match',
    Synchronization: 'guesty-synchronization',
    Settings: 'guesty-settings',
  };

  ['2024-25', '2025-26', '2026-27'].map(year => {
    mainScreens[`Annual Credits ${year}`] = `annual-credits-${year}`;
  });

  return mainScreens;
};

function App(): JSX.Element {
  const [initializing, setInitializing] = useState(true);
  const [listenUser, setListenUser] = useState(false);
  const [user, setUser] = useState<User>(null);
  const appSettings = useAppSettings();

  /** Listen for auth state changes */
  useEffect(() => {
    const authListener = auth().onAuthStateChanged(result => {
      setUser(result);
      if (initializing && !listenUser) {
        setInitializing(false);
        setListenUser(true);
      }
    });

    return () => {
      if (authListener) {
        authListener();
      }
    };
  }, [initializing, listenUser]);

  /** Listen for user changes */
  useEffect(() => {
    let userListener: () => void;

    if (listenUser) {
      // TODO @react-native-firebase/auth provides `onUserChanged` which is this and more.
      // what else can we add and still be web-compatible?
      userListener = auth().onIdTokenChanged(result => {
        setUser(result);
      });
    }

    return () => {
      if (userListener) {
        userListener();
      }
    };
  }, [listenUser]);

  if (initializing) {
    let waiting = true;
    setTimeout(() => {
      waiting = false;
    }, 1000);

    return (
      <View
        style={[
          styles.loadingContainer,
          {backgroundColor: appSettings.currentTheme.colors.background},
        ]}>
        {!waiting && (
          <Fragment>
            <Headline
              style={[
                styles.padded,
                {color: appSettings.currentTheme.colors.text},
              ]}>
              {appSettings.t('loading')}...
            </Headline>
            <ActivityIndicator
              size={'large'}
              theme={{
                ...appSettings.currentTheme,
                colors: {primary: appSettings.currentTheme.colors.accent},
              }}
            />
          </Fragment>
        )}
      </View>
    );
  }

  // By default we do not want to query APIs that are billable per call, disable focus re-fetch
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        retry: false,
      },
    },
  });

  const getNavigationScreens = () => {
    if (!user) {
      return {
        screens: {
          // Our signed-out stack has these:
          // SignIn: 'account/login',
          CreateAccount: 'account/create',
          ForgotPassword: 'account/password/forgot',
          PhoneSignIn: 'account/phone/login',

          // Used as catch-all - we try for a public profile, and go 404 if it does not work
          // NotFound: '*',
          login: '*',
        },
      };
    } else {
      return {
        screens: {
          // FIXME signed-in stack isn't configured at all yet
          App: {
            path: '',
            screens: getAppScreens(),
          },
          ProfileSettingsModal: {
            path: 'settings',
            screens: {
              ProfileSettingsScreen: 'profile',
              AccountSwitchScreen: 'profile/switch',
              Settings: 'profile/edit',
            },
          },

          // Used as catch-all - there is a "Home" in signed-in and signed-out stacks!
          // FIXME this is horrible at the moment - if you hit the 404, the nav menus are messed up.
          NotFound: '*',
        },
      };
    }
  };

  function container(children: ReactNode | ReactNode[]) {
    return (
      <SafeAreaProvider initialMetrics={initialWindowMetrics}>
        <QueryClientProvider client={queryClient}>
          <PaperProvider theme={appSettings.currentTheme}>
            <AlertsProvider>
              <ErrorBoundary>
                <NavigationContainer
                  linking={{
                    prefixes: [
                      'equity-residences.web.app',
                      'equity-residences-dev.web.app',
                      'mikehardy.github.io/equityresidences',
                      'localhost',
                    ],
                    config: getNavigationScreens(),
                  }}
                  ref={mainNavigatorRef}
                  documentTitle={{
                    formatter: (options, route) =>
                      `${appJson.displayName}${
                        options?.title || route?.name
                          ? ' - ' + (options?.title ?? route?.name)
                          : ' '
                      }`,
                  }}
                  theme={appSettings.currentTheme}>
                  {children}
                </NavigationContainer>
              </ErrorBoundary>
            </AlertsProvider>
          </PaperProvider>
        </QueryClientProvider>
      </SafeAreaProvider>
    );
  }

  class ErrorBoundary extends Component {
    constructor(props: Record<string, unknown>) {
      super(props);
      this.state = {hasError: false};
    }

    static getDerivedStateFromError() {
      // Update state so the next render will show the fallback UI.
      return {hasError: true};
    }

    // componentDidCatch(error, errorInfo) {
    //   // You can also log the error to an error reporting service
    //   logErrorToMyService(error, errorInfo);
    // }

    render() {
      // @ts-ignore
      if (this.state.hasError) {
        // You can render any custom fallback UI
        return (
          <View style={styles.loadingContainer}>
            <Subheading style={styles.grayText}>
              Oh no! Something went wrong.
            </Subheading>
            <Button
              mode="outlined"
              onPress={() => {
                this.setState({hasError: false});
              }}>
              Try Again?
            </Button>
          </View>
        );
      }

      return this.props.children;
    }
  }

  return container(
    user ? (
      <UserContext.Provider value={user}>
        <SignedInStack />
      </UserContext.Provider>
    ) : (
      <SignedOutStack />
    ),
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignContent: 'center',
    // alignSelf: 'center',
    alignItems: 'center',
    // textAlignVertical: true,
  },
  padded: {
    padding: 40,
  },
  grayText: {
    color: 'gray',
  },
});

export default App;
